import type { SyncDelta } from '@couplespace/sync';

import type { LocalDatabase } from './types';

/**
 * A server delta that failed to apply locally. We keep the full delta (rather
 * than dropping it and advancing the cursor, which silently loses data) so it
 * can be retried and, until it succeeds, counted and shown to the user. Keyed
 * by (table, row) so a newer delta for the same row replaces an older failure.
 */
export type QuarantineEntry = {
  tableName: string;
  rowId: string;
  seq: number;
  operation: string;
  delta: SyncDelta;
  error: string | null;
  attempts: number;
  firstSeen: number;
  lastAttempt: number;
};

type QuarantineRow = {
  table_name: string;
  row_id: string;
  seq: number;
  operation: string;
  delta_json: string;
  error: string | null;
  attempts: number;
  first_seen: number;
  last_attempt: number;
};

export async function quarantineDelta(
  database: LocalDatabase,
  delta: SyncDelta,
  error: string,
): Promise<void> {
  const now = Date.now();
  await database.runAsync(
    `INSERT INTO sync_quarantine
       (table_name, row_id, seq, operation, delta_json, error, attempts, first_seen, last_attempt)
     VALUES (?, ?, ?, ?, ?, ?, 1, ?, ?)
     ON CONFLICT(table_name, row_id) DO UPDATE SET
       seq = excluded.seq,
       operation = excluded.operation,
       delta_json = excluded.delta_json,
       error = excluded.error,
       attempts = attempts + 1,
       last_attempt = excluded.last_attempt`,
    delta.table,
    delta.rowId,
    delta.seq,
    delta.operation,
    JSON.stringify(delta),
    error,
    now,
    now,
  );
}

export async function clearQuarantine(
  database: LocalDatabase,
  tableName: string,
  rowId: string,
): Promise<void> {
  await database.runAsync(
    `DELETE FROM sync_quarantine WHERE table_name = ? AND row_id = ?`,
    tableName,
    rowId,
  );
}

export async function clearAllQuarantine(database: LocalDatabase): Promise<void> {
  await database.runAsync(`DELETE FROM sync_quarantine`);
}

export async function countQuarantine(database: LocalDatabase): Promise<number> {
  const row = await database.getFirstAsync<{ n: number }>(
    `SELECT COUNT(*) AS n FROM sync_quarantine`,
  );
  return row?.n ?? 0;
}

/** Set of "table\u0000rowId" keys currently quarantined, for cheap membership checks. */
export async function quarantinedKeySet(database: LocalDatabase): Promise<Set<string>> {
  const rows = await database.getAllAsync<{ table_name: string; row_id: string }>(
    `SELECT table_name, row_id FROM sync_quarantine`,
  );
  return new Set(rows.map((r) => `${r.table_name}\u0000${r.row_id}`));
}

export async function listQuarantine(database: LocalDatabase): Promise<QuarantineEntry[]> {
  const rows = await database.getAllAsync<QuarantineRow>(
    `SELECT * FROM sync_quarantine ORDER BY last_attempt DESC`,
  );
  return rows.map((row) => ({
    tableName: row.table_name,
    rowId: row.row_id,
    seq: row.seq,
    operation: row.operation,
    delta: JSON.parse(row.delta_json) as SyncDelta,
    error: row.error,
    attempts: row.attempts,
    firstSeen: row.first_seen,
    lastAttempt: row.last_attempt,
  }));
}
